import React, { useState, useEffect, useRef } from "react";
import { Col, Container, Row, Card, Table, Button } from "react-bootstrap";
import { TextInputForm } from "../components/Forms";
import { useNavigate, useLocation } from "react-router-dom";
import API_DOMAIN from "../config/config";
import { Buttons, ClickButton } from "../components/Buttons";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { PageHead } from "../components/PageTitle";
import { PDFDownloadLink } from "@react-pdf/renderer";
import StaffPDF from "./StaffPDF";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StaffCreation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { type, rowData } = location.state || {};

  const isSubmittingRef = useRef(false);

  // --- Filter States ---
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filteredHistory, setFilteredHistory] = useState([]);

  const initialState =
    type === "edit" || type === "view"
      ? {
          staff_name: rowData?.staff_name || "",
          mobile_number: rowData?.mobile_number || "",
          role: rowData?.role || "",
          place: rowData?.place || "",
          wages_amount: rowData?.wages_amount || "",
          advance_balance: rowData?.advance_balance || 0,
        }
      : {
          staff_name: "",
          mobile_number: "",
          role: "",
          place: "",
          wages_amount: "",
        };

  const [formData, setFormData] = useState(initialState);
  console.log("form data values", formData);

  useEffect(() => {
    if (rowData?.advance_history) {
      const today = new Date().toISOString().split("T")[0];
      const yesterdayDate = new Date();
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);
      const yesterday = yesterdayDate.toISOString().split("T")[0];

      let filtered = rowData.advance_history.filter((item) => {
        return item.entry_date === today || item.entry_date === yesterday;
      });

      // Remove zero amount entries
      filtered = filtered.filter((item) => Number(item.amount) !== 0);

      setFilteredHistory(filtered);
    }
  }, [rowData]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target ? e.target.value : e.value;
    setFormData({ ...formData, [fieldName]: value });
  };

  // --- Filter Logic ---
  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.info("Please select both From and To dates");
      return;
    }
    const filtered = rowData.advance_history.filter((item) => {
      return item.entry_date >= startDate && item.entry_date <= endDate;
    });
    setFilteredHistory(filtered);
  };

  const handleUndoFilter = () => {
    setStartDate("");
    setEndDate("");
    const today = new Date().toISOString().split("T")[0];
    const yesterdayDate = new Date();
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);
    const yesterday = yesterdayDate.toISOString().split("T")[0];

    const defaultView =
      rowData?.advance_history.filter(
        (item) => item.entry_date === today || item.entry_date === yesterday
      ) || [];

    setFilteredHistory(defaultView);
  };

  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(`${API_DOMAIN}/staff.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, action: "createStaff" }),
      });
      const responseData = await response.json();
      if (responseData.head.code === 200) {
        toast.success("Staff successfully added!");
        setTimeout(() => {
          isSubmittingRef.current = false;
          setLoading(false);
          navigate("/console/staff");
        }, 2000);
      } else {
        isSubmittingRef.current = false;
        setLoading(false);
      }
    } catch (error) {
      toast.error("Something went wrong.");
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async () => {
    if (isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(`${API_DOMAIN}/staff.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          edit_staff_id: rowData.id,
          action: "updateStaff",
        }),
      });
      const responseData = await response.json();
      if (responseData.head.code === 200) {
        toast.success("Staff updated successfully!");
        setTimeout(() => {
          isSubmittingRef.current = false;
          setLoading(false);
          navigate("/console/staff");
        }, 2000);
      } else {
        isSubmittingRef.current = false;
        setLoading(false);
      }
    } catch (error) {
      toast.error("Something went wrong.");
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const isDisabled = loading || isSubmittingRef.current;

  return (
    <div className="main p-0 my-3 pt-3">
      <div className="p-2 fixed">
        <PageHead
          pagehead={
            type === "view"
              ? "Staff Details"
              : type === "edit"
              ? "Edit Staff"
              : "Add Staff"
          }
          onClick={() => navigate(-1)}
        />
      </div>

      <div className="inside-content p-3">
        <Container fluid>
          {/* --- VIEW MODE: FILTERS & DETAILS --- */}
          {type === "view" && (
            <>
              <Row className="align-items-end g-3 mb-4">
                <Col md={2} xs={6}>
                  <label className="small fw-bold mb-1 text-muted">
                    From Date
                  </label>
                  <DatePicker
                    selected={startDate ? new Date(startDate) : null}
                    onChange={(date) =>
                      setStartDate(date ? date.toISOString().split("T")[0] : "")
                    }
                    dateFormat="dd-MM-yyyy"
                    className="form-control form-control-sm border-secondary-subtle"
                    placeholderText="dd-mm-yyyy"
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </Col>

                <Col md={2} xs={6}>
                  <label className="small fw-bold mb-1 text-muted">
                    To Date
                  </label>
                  <DatePicker
                    selected={endDate ? new Date(endDate) : null}
                    onChange={(date) =>
                      setEndDate(date ? date.toISOString().split("T")[0] : "")
                    }
                    dateFormat="dd-MM-yyyy"
                    className="form-control form-control-sm border-secondary-subtle"
                    placeholderText="dd-mm-yyyy"
                    isClearable
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                  />
                </Col>

                <Col xs={12} md={8}>
                  <div className="d-flex gap-2">
                    <Buttons
                      lable={<> Apply</>}
                      size="sm"
                      className="px-3 shadow-sm"
                      onClick={handleApplyFilter}
                    />

                    <Buttons
                      lable={<> Undo</>}
                      size="sm"
                      className="px-3 shadow-sm"
                      onClick={handleUndoFilter}
                    />

                    <PDFDownloadLink
                      document={
                        <StaffPDF
                          data={{
                            ...rowData,
                            advance_history: filteredHistory,
                          }}
                        />
                      }
                      fileName={`${formData.staff_name}_Report.pdf`}
                    >
                      {({ loading }) => <Buttons lable={<> Download PDF</>} />}
                    </PDFDownloadLink>
                  </div>
                </Col>
              </Row>

              {/* Staff Details Card */}
              <Row className="mb-4">
                <Col lg="12">
                  <Card className="shadow-sm border-0 bg-light p-3">
                    <Row className="text-center text-md-start">
                      <Col md={3} className="border-end border-2">
                        <small className="text-muted d-block">Staff Name</small>
                        <h4 className="fw-bold mb-0">{formData.staff_name}</h4>
                      </Col>
                      <Col md={3} className="border-end border-2">
                        <small className="text-muted d-block">Role</small>
                        <h4 className="text-capitalize mb-0">
                          {formData.role}
                        </h4>
                      </Col>
                      <Col md={3} className="border-end border-2">
                        <small className="text-muted d-block">
                          Daily Wages
                        </small>
                        <h4 className="text-success mb-0">
                          ₹{formData.wages_amount}
                        </h4>
                      </Col>
                      <Col md={3}>
                        <small className="text-muted d-block">
                          Advance Balance
                        </small>
                        <h4 className="text-danger mb-0">
                          ₹{formData.advance_balance}
                        </h4>
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* Advance History Table */}
              <div className="mt-4">
                <h5 className="mb-3 fw-bold">Advance History</h5>
                <div className="table-responsive shadow-sm border rounded bg-white">
                  <Table hover className="mb-0">
                    <thead className="table-dark">
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Mode</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory?.length > 0 ? (
                        filteredHistory
                          .filter((item) => Number(item.amount) !== 0) // Hide zero amounts
                          .map((item, index) => (
                            <tr key={index}>
                              <td>{formatDate(item.entry_date)}</td>
                              <td>
                                <span
                                  className={`badge ${
                                    item.type === "add"
                                      ? "bg-warning text-dark"
                                      : "bg-info"
                                  }`}
                                >
                                  {item.type}
                                </span>
                              </td>
                              <td>{item.recovery_mode || "N/A"}</td>
                              <td
                                className={`text-end fw-bold ${
                                  item.type === "add"
                                    ? "text-success"
                                    : "text-danger"
                                }`}
                              >
                                {item.type === "add"
                                  ? `+ ${item.amount}`
                                  : `- ${item.amount}`}
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            className="text-center text-muted py-4"
                          >
                            No records found for the selected range
                          </td>
                        </tr>
                      )}

                      {/* Show message if all entries were zero after filtering */}
                      {filteredHistory?.length > 0 &&
                        filteredHistory.every(
                          (item) => Number(item.amount) === 0
                        ) && (
                          <tr>
                            <td
                              colSpan="4"
                              className="text-center text-muted py-4"
                            >
                              No advance transactions recorded in the selected
                              period
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </>
          )}

          {/* --- EDIT/ADD MODE --- */}
          {type !== "view" && (
            <Row className="g-3">
              <Col lg="4">
                <TextInputForm
                  labelname="Staff Name"
                  value={formData.staff_name}
                  onChange={(e) => handleInputChange(e, "staff_name")}
                />
              </Col>
              <Col lg="4">
                <TextInputForm
                  labelname="Mobile Number"
                  value={formData.mobile_number}
                  onChange={(e) => handleInputChange(e, "mobile_number")}
                />
              </Col>
              <Col lg="4">
                <TextInputForm
                  labelname="Role"
                  value={formData.role}
                  onChange={(e) => handleInputChange(e, "role")}
                />
              </Col>
              <Col lg="4">
                <TextInputForm
                  labelname="Place"
                  value={formData.place}
                  onChange={(e) => handleInputChange(e, "place")}
                />
              </Col>
              <Col lg="4">
                <TextInputForm
                  labelname="Wages Amount"
                  value={formData.wages_amount}
                  onChange={(e) => handleInputChange(e, "wages_amount")}
                />
              </Col>
            </Row>
          )}

          {/* --- ACTION BUTTONS --- */}
          <div className="text-center py-4">
            <ToastContainer
              position="top-center"
              autoClose={3000}
              theme="light"
            />
            {type !== "view" ? (
              <div className="d-flex justify-content-center gap-3">
                {/* UPDATED: Use combined disabled logic */}
                <ClickButton
                  label={type === "edit" ? "Update" : "Submit"}
                  onClick={type === "edit" ? handleUpdateSubmit : handleSubmit}
                  disabled={isDisabled}
                />
                <ClickButton
                  label="Cancel"
                  onClick={() => navigate("/console/staff")}
                />
              </div>
            ) : (
              <ClickButton
                label="Back to List"
                onClick={() => navigate("/console/staff")}
              />
            )}
          </div>
        </Container>
      </div>
    </div>
  );
};

export default StaffCreation;
